import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InquiryCard from "components/inquiry/inquiry_card";
import { useUser } from "util/context/user_context";
import { getInquiryData } from "firebase_/firebase_function/firebase_inquiry";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  green: "#059669",
  bg: "#F7FAF7",
  amber50: "#FFFBEB",
  amber800: "#92400E",
};

const ITEMS_PER_PAGE = 5;
const PAGE_GROUP = 5;

const FILTERS = [
  { key: "all", label: "전체" },
  { key: "pending", label: "답변 대기" },
  { key: "done", label: "답변 완료" },
];

function SkeletonCard() {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: 18,
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          background: "#F3F4F6",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ height: 14, width: "30%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 18, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
        <div style={{ height: 12, width: "50%", background: "#F3F4F6", borderRadius: 6 }} />
      </div>
    </div>
  );
}

function EmptyState({ onWrite }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 20px",
        background: "#fff",
        borderRadius: 16,
        border: `1px dashed ${C.line}`,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 9999,
          background: C.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 34,
        }}
      >
        💬
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
        문의 내역이 없어요
      </div>
      <div style={{ fontSize: 13, color: C.sub, textAlign: "center" }}>
        궁금한 점이 있으시면 언제든 문의해 주세요.
        <br />
        영업일 기준 빠르게 답변드립니다.
      </div>
      <button
        type="button"
        onClick={onWrite}
        style={{
          marginTop: 8,
          padding: "10px 20px",
          borderRadius: 10,
          border: "none",
          background: C.greenDark,
          color: "#fff",
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        문의 작성하기
      </button>
    </div>
  );
}

function SimplePagenation({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    fontSize: 14,
  };

  return (
    <div style={{ display: "flex", gap: 20, margin: "24px 0", justifyContent: "center", alignItems: "center" }}>
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        style={{ ...btnBase, cursor: canPrev ? "pointer" : "default", color: canPrev ? C.sub : "#c3c3c3" }}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>이전</span>
      </button>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {numbers.map((n) => {
          const active = n === page;
          return (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: 9999,
                backgroundColor: active ? C.green : "transparent",
                color: active ? "#fff" : C.body,
                fontWeight: active ? 700 : 500,
                border: "none",
                transition: "all 0.15s ease",
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        style={{ ...btnBase, cursor: canNext ? "pointer" : "default", color: canNext ? C.sub : "#c3c3c3" }}
      >
        <span>다음</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
}

export default function DesktopInquiryScreen({ isMobile }) {
  const { userData } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await getInquiryData(undefined, userData?.id);
        if (!cancelled) setAllItems(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setAllItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const filtered = useMemo(() => {
    if (filter === "all") return allItems;
    if (filter === "pending") return allItems.filter((it) => String(it?.status) !== "1");
    if (filter === "done") return allItems.filter((it) => String(it?.status) === "1");
    return allItems;
  }, [allItems, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentItems = useMemo(
    () => filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(Math.max(1, totalPages));
  }, [totalPages, page]);

  useEffect(() => {
    sessionStorage.setItem(`page_${location.pathname}`, page);
    window.scrollTo({ top: 0 });
  }, [page, location.pathname]);

  const stats = useMemo(() => {
    const total = allItems.length;
    const done = allItems.filter((it) => String(it?.status) === "1").length;
    return { total, pending: total - done, done };
  }, [allItems]);

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        maxWidth: 1100,
        margin: "0 auto",
        padding: isMobile ? "24px 16px 80px" : "32px 20px 80px",
        minHeight: "100vh",
        background: C.bg,
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: isMobile ? 22 : 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            문의 내역
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            서비스 이용 중 궁금한 점을 등록하고 답변을 확인하세요.
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/inquiry/write")}
          style={{
            padding: "10px 18px",
            borderRadius: 10,
            border: "none",
            background: C.greenDark,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span> 문의 작성하기
        </button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <StatCard label="전체 문의" value={stats.total} />
        <StatCard label="답변 대기" value={stats.pending} accent={C.amber800} />
        <StatCard label="답변 완료" value={stats.done} accent={C.greenDark} />
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              style={{
                padding: "8px 16px",
                borderRadius: 9999,
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color: active ? "#fff" : C.body,
                background: active ? C.greenDark : "#fff",
                border: `1px solid ${active ? C.greenDark : C.line}`,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : currentItems.length > 0 ? (
          currentItems.map((item, index) => (
            <InquiryCard key={item?.docId || index} inquiryData={item} />
          ))
        ) : (
          <EmptyState onWrite={() => navigate("/inquiry/write")} />
        )}
      </div>

      <SimplePagenation page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: "14px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
    </div>
  );
}
